import { differenceInCalendarDays, format, isValid, parseISO, type Locale } from 'date-fns'
import { ca, enGB, es, fr } from 'date-fns/locale'

/**
 * Punt d'entrada per formatar **dates de calendari** (check-in, check-out, data de
 * naixement): F4 de `plans/guest-data-quality.md`.
 *
 * La DB desa `checkIn` com a mitjanit UTC (`2026-08-10T00:00:00.000Z`). Amb
 * `new Date(iso)` + `toLocaleDateString`, un navegador a Buenos Aires (UTC−3) hi veu
 * el 9 d'agost. Aquí es talla la part `yyyy-MM-dd` i es llegeix com a data local amb
 * `parseISO`: el 10 és el 10 a qualsevol zona. Els instants de veritat (hora d'un
 * missatge, d'un enviament) no passen per aquí.
 */

const LOCALE_BY_LANG: Record<string, Locale> = { ca, es, en: enGB, fr }

/** `ca` → català, `es` → castellà, `en` → anglès britànic, `fr` → francès; per defecte, castellà. */
export function langToLocale(lang: string | null | undefined): Locale {
  const base = (lang ?? '').split('-')[0].toLowerCase()
  return LOCALE_BY_LANG[base] ?? es
}

/** Llegeix la part `yyyy-MM-dd` d'un ISO com a data de calendari local. `null` si no n'és. */
export function parseCalendarDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const date = parseISO(iso.slice(0, 10))
  return isValid(date) ? date : null
}

// date-fns 4 escriu «10 de agost 2026» en català: sense apòstrof i sense «de» davant de l'any
const CA_FULL_DATE_PATTERN = "EEEE, d 'de' MMMM 'de' y"

function elideCatalan(text: string): string {
  return text.replace(/\bde (?=(abr|ag|oct))/g, 'd’')
}

/**
 * Formata una data de calendari amb el locale de l'idioma. `pattern` és de `date-fns`
 * (per defecte `PPPP`, la data llarga amb dia de la setmana). Sense data, `''`.
 */
export function formatCalendarDate(
  iso: string | null | undefined,
  lang: string | null | undefined,
  pattern = 'PPPP',
): string {
  const date = parseCalendarDate(iso)
  if (!date) return ''

  const locale = langToLocale(lang)
  if (locale !== ca) return format(date, pattern, { locale })

  return elideCatalan(format(date, pattern === 'PPPP' ? CA_FULL_DATE_PATTERN : pattern, { locale }))
}

/** Nits entre dues dates de calendari (dies de calendari, sense horari d'estiu pel mig). */
export function calendarNights(
  checkIn: string | null | undefined,
  checkOut: string | null | undefined,
): number {
  const start = parseCalendarDate(checkIn)
  const end = parseCalendarDate(checkOut)
  if (!start || !end) return 0
  return differenceInCalendarDays(end, start)
}
